import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "chat_translation_preferences";
const PREFERENCES_CHANGED_EVENT = "chat-translation-preferences-changed";

export interface TranslationPreferences {
  neverTranslateEnglish: boolean;
  showGuestOriginalMessages: boolean;
}

const DEFAULT_PREFERENCES: TranslationPreferences = {
  neverTranslateEnglish: false,
  showGuestOriginalMessages: false,
};

const readPreferences = (): TranslationPreferences => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (!value) return DEFAULT_PREFERENCES;

    const parsed: unknown = JSON.parse(value);
    if (!parsed || typeof parsed !== "object") return DEFAULT_PREFERENCES;

    const preferences = parsed as Partial<TranslationPreferences>;
    return {
      neverTranslateEnglish: preferences.neverTranslateEnglish === true,
      showGuestOriginalMessages: preferences.showGuestOriginalMessages === true,
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
};

const writePreferences = (preferences: TranslationPreferences): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  window.dispatchEvent(new Event(PREFERENCES_CHANGED_EVENT));
};

export const useTranslationPreferences = () => {
  const [preferences, setPreferences] =
    useState<TranslationPreferences>(readPreferences);

  useEffect(() => {
    const syncPreferences = () => setPreferences(readPreferences());

    window.addEventListener("storage", syncPreferences);
    window.addEventListener(PREFERENCES_CHANGED_EVENT, syncPreferences);

    return () => {
      window.removeEventListener("storage", syncPreferences);
      window.removeEventListener(PREFERENCES_CHANGED_EVENT, syncPreferences);
    };
  }, []);

  const updatePreferences = useCallback(
    (nextPreferences: Partial<TranslationPreferences>) => {
      const nextValue = { ...preferences, ...nextPreferences };
      setPreferences(nextValue);
      writePreferences(nextValue);
    },
    [preferences],
  );

  return { preferences, updatePreferences };
};
